import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { handleGet } from "../controllers/controller.all";
import SuperDashboard from "./superadmin/view.superDashboard";

export default function Home() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = useSelector((state) => state.userReducer?.token) || "";
    const ecoleId = user?.ecole?._id || (typeof user?.ecole === "string" ? user?.ecole : "");
    const fetchRef = useRef(null); // track latest fetch cycle

    const [stats, setStats] = useState({
        students: "--",
        teachers: "--",
        classes: "--",
        matieres: "--"
    });
    const [history, setHistory] = useState([]);

    useEffect(() => {
        // Validate both token and ecoleId before firing any request.
        // An empty/falsy value means the user is logged out or the state
        // hasn't settled yet – bail out immediately to avoid 403/CORS errors.
        if (!token || !ecoleId || user?.role === "SUPER_ADMIN") return;

        // Each render cycle gets its own fetch id; stale cycles are ignored.
        const currentFetch = {};
        fetchRef.current = currentFetch;

        const fetchDashboardData = async () => {
            // Snapshot token + ecoleId so that even if Redux updates mid-flight
            // we use consistent values throughout this fetch cycle.
            const tok = token;
            const eid = ecoleId;

            // Helper: run handleGet only if this fetch cycle is still current.
            const safeGet = (endpoint, cb) => {
                if (fetchRef.current !== currentFetch) return Promise.resolve();
                return handleGet(endpoint, cb, tok);
            };

            // Fetch students
            await safeGet(`/user/s/all/eleves/${eid}`, (res) => {
                if (res?.users) setStats(prev => ({ ...prev, students: res.users.length }));
            });

            // Fetch teachers
            await safeGet(`/user/s/all/professeurs/${eid}`, (res) => {
                if (res?.users) setStats(prev => ({ ...prev, teachers: res.users.length }));
            });

            // Fetch classes
            await safeGet(`/classe/ecole/${eid}`, (res) => {
                if (res?.classes) setStats(prev => ({ ...prev, classes: res.classes.length }));
            });

            // Fetch matieres
            await safeGet(`/matiere/ecole/${eid}`, (res) => {
                if (res?.matieres) {
                    const mat = res.matieres.filter(m => m.ecole === eid || m.ecole?._id === eid);
                    setStats(prev => ({ ...prev, matieres: mat.length }));
                }
            });

            // Fetch historique
            await safeGet(`/historique/ecole/${eid}`, (res) => {
                const dataList = res?.historiques || res?.historique || (Array.isArray(res) ? res : []);
                if (dataList && dataList.length > 0) {
                    const sorted = [...dataList].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 10);
                    setHistory(sorted);
                }
            });
        };

        fetchDashboardData();
        // eslint-disable-next-line
    }, [ecoleId, token]);

    if (user?.role === "SUPER_ADMIN") {
        return <SuperDashboard />;
    }

    return (
        <div className="flex flex-col gap-6">
            <header className="flex flex-col gap-1">
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">Tableau de bord</h1>
                <p className="text-gray-500">Bienvenue, {user?.prenom || 'Admin'} ! Voici le résumé des activités.</p>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
                <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6 shadow-sm min-w-0">
                    <dt className="truncate text-xs sm:text-sm font-medium text-gray-500">Total Étudiants</dt>
                    <dd className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 truncate">{stats.students}</dd>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6 shadow-sm min-w-0">
                    <dt className="truncate text-xs sm:text-sm font-medium text-gray-500">Total Professeurs</dt>
                    <dd className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 truncate">{stats.teachers}</dd>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6 shadow-sm min-w-0">
                    <dt className="truncate text-xs sm:text-sm font-medium text-gray-500">Total classes</dt>
                    <dd className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 truncate">{stats.classes}</dd>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6 shadow-sm min-w-0">
                    <dt className="truncate text-xs sm:text-sm font-medium text-gray-500">Total matières</dt>
                    <dd className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 truncate">{stats.matieres}</dd>
                </div>
            </div>
            <div className="mt-8 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                    <h2 className="text-lg font-medium text-gray-900">Historique des Activités</h2>
                </div>
                <div className="divide-y divide-gray-200">
                    {history.length === 0 ? (
                        <div className="p-10 flex items-center justify-center text-gray-400 border-dashed border-2 border-gray-100 m-6 rounded-lg">
                            <span>Aucune donnée d'historique trouvée.</span>
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                            {history.map((item, index) => {
                                const actorName = item.acteur_id.prenom ? `${item.acteur_id.prenom || ''} ${item.acteur_id.nom || ''}`.trim() : 'Système';
                                const date = item.createdAt ? new Date(item.createdAt).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '';

                                return (
                                    <li key={item._id || index} className="p-4 hover:bg-gray-50 transition flex items-start gap-4">
                                        <div className="flex-shrink-0 mt-1">
                                            <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2"></div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900">
                                                {item.action} - <span className="text-indigo-600">{item.entite_type}</span>
                                            </p>
                                            <p className="text-sm text-gray-500 mt-0.5">
                                                {item.details}
                                            </p>
                                            <p className="text-sm text-gray-500 mt-0.5">
                                                {`Action effectuée par ${actorName}`}
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap">
                                            {date}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    )
}